import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  // NOTE: We intentionally do NOT set `withCredentials: true`. Auth uses
  // a Bearer JWT in the Authorization header (set by the request
  // interceptor below), so cookies are not needed for any frontend call.
  // Sending credentials would also force the browser to enforce strict
  // CORS — Emergent's edge ingress overrides our backend's
  // `Access-Control-Allow-Origin` to `*`, which is invalid in
  // combination with `Access-Control-Allow-Credentials: true` and was
  // causing every login/signup attempt to fail on iOS Safari with
  // "Couldn't reach the server".
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("pg_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Cold-start retry: if the deployed backend is sleeping, the first request
// can fail with a network error or 502/503/504 while it spins up. Retry
// once after a short delay so users don't see a "couldn't reach the server"
// toast on a cold container.
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error?.config || {};
    if (cfg.__retried) return Promise.reject(error);
    const status = error?.response?.status;
    const isColdStart =
      !error?.response ||
      error?.code === "ERR_NETWORK" ||
      error?.code === "ECONNABORTED" ||
      status === 502 ||
      status === 503 ||
      status === 504;
    if (!isColdStart) return Promise.reject(error);
    cfg.__retried = true;
    await new Promise((res) => setTimeout(res, 1500));
    return api.request(cfg);
  }
);

// Keepalive ping: hit /health every 4 minutes while the tab is open so the
// deployed container stays warm. Cheap (no DB work), idempotent, and
// silently no-ops on failure.
let _keepaliveStarted = false;
export const startKeepalive = () => {
  if (_keepaliveStarted || typeof window === "undefined") return;
  _keepaliveStarted = true;
  const ping = () => {
    api.get("/health").catch(() => {});
  };
  // First ping after 30s so we don't compete with initial page-load XHRs.
  setTimeout(ping, 30000);
  setInterval(ping, 4 * 60 * 1000);
};

export const storeAuth = ({ token, user }) => {
  if (token) localStorage.setItem("pg_token", token);
  if (user) localStorage.setItem("pg_user", JSON.stringify(user));
};

export const clearAuth = () => {
  localStorage.removeItem("pg_token");
  localStorage.removeItem("pg_user");
};

export const loadUser = () => {
  try {
    const raw = localStorage.getItem("pg_user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
